import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, ShieldCheck, Loader2, Eye, Check, X, Clock, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const TYPES = [
  { v: "access", l: "Access" },
  { v: "deletion", l: "Deletion" },
  { v: "rectification", l: "Rectification" },
  { v: "portability", l: "Portability" },
];

const STATUS_META = {
  pending: { l: "Pending", cls: "text-yellow-500 border-yellow-500/30 bg-yellow-500/5", Icon: Clock },
  in_review: { l: "In Review", cls: "text-primary border-primary/30 bg-primary/5", Icon: Eye },
  resolved: { l: "Resolved", cls: "text-emerald-500 border-emerald-500/30 bg-emerald-500/5", Icon: Check },
  rejected: { l: "Rejected", cls: "text-destructive border-destructive/30 bg-destructive/5", Icon: X },
};

const FILTERS = ["all", "pending", "in_review", "resolved", "rejected", "overdue"];
const inputCls = "bg-background border-border rounded-sm text-sm focus-visible:ring-1 focus-visible:ring-primary";

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  return (
    <span className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.08em] px-2 py-1 rounded-sm border ${m.cls}`}>
      <m.Icon className="w-3 h-3" /> {m.l}
    </span>
  );
}

function SlaCell({ r }) {
  const due = r.due_date ? new Date(r.due_date).toLocaleDateString() : "—";
  if (r.status === "resolved" || r.status === "rejected") {
    return <div className="font-mono text-[11px] text-muted-foreground">{due}</div>;
  }
  if (r.overdue) {
    return (
      <div>
        <div className="font-mono text-[11px] text-muted-foreground">{due}</div>
        <span className="inline-flex items-center gap-1 mt-0.5 font-mono text-[10px] uppercase tracking-[0.06em] text-destructive">
          <AlertTriangle className="w-3 h-3" /> {Math.abs(r.days_remaining)}d overdue
        </span>
      </div>
    );
  }
  const urgent = r.days_remaining != null && r.days_remaining <= 7;
  return (
    <div>
      <div className="font-mono text-[11px] text-muted-foreground">{due}</div>
      <span className={`font-mono text-[10px] ${urgent ? "text-yellow-500" : "text-muted-foreground"}`}>
        {r.days_remaining}d left
      </span>
    </div>
  );
}

export default function PrivacyRequests() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ request_type: "access", subject_email: "", subject_name: "", notes: "" });

  const { data } = useQuery({
    queryKey: ["privacy"],
    queryFn: async () => (await api.get("/privacy-requests")).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["privacy"] });
    qc.invalidateQueries({ queryKey: ["kpis"] });
  };

  const create = async () => {
    if (!form.subject_email) return toast.error("Subject email is required");
    setSaving(true);
    try {
      await api.post("/privacy-requests", form);
      toast.success("Privacy request logged");
      setOpen(false);
      setForm({ request_type: "access", subject_email: "", subject_name: "", notes: "" });
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const transition = async (id, updates, msg) => {
    try {
      const { data: updated } = await api.patch(`/privacy-requests/${id}`, updates);
      toast.success(msg);
      setDetail(updated);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const counts = data?.counts || {};
  const allRequests = data?.requests || [];
  const requests =
    filter === "all"
      ? allRequests
      : filter === "overdue"
      ? allRequests.filter((r) => r.overdue)
      : allRequests.filter((r) => r.status === filter);

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Filter + counts */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-2 flex-wrap" data-testid="privacy-filters">
          {FILTERS.map((s) => {
            const label = s === "all" ? "All" : s === "overdue" ? "Overdue" : STATUS_META[s].l;
            const count = s === "all" ? undefined : counts[s] ?? 0;
            const active = filter === s;
            const overdueStyle = s === "overdue";
            return (
              <button
                key={s}
                data-testid={`filter-${s}`}
                onClick={() => setFilter(s)}
                className={`font-mono text-[11px] uppercase tracking-[0.08em] px-3 py-1.5 rounded-sm border transition-colors duration-200 ${
                  active
                    ? overdueStyle
                      ? "border-destructive/40 bg-destructive/10 text-destructive"
                      : "border-primary/40 bg-primary/10 text-white"
                    : "border-border text-muted-foreground hover:text-white"
                }`}
              >
                {label}
                {count !== undefined && <span className="ml-1.5 text-muted-foreground">{count}</span>}
              </button>
            );
          })}
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-request-btn" className="rounded-sm bg-primary hover:bg-primary/90 transition-colors duration-200">
              <Plus className="w-4 h-4 mr-1" /> New Request
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border rounded-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-primary" /> Log Data-Subject Request
              </DialogTitle>
              <DialogDescription>An SLA due date is assigned automatically based on request type.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Request Type</Label>
                <Select value={form.request_type} onValueChange={(v) => setForm((f) => ({ ...f, request_type: v }))}>
                  <SelectTrigger data-testid="req-type" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPES.map((t) => (
                      <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Subject Email</Label>
                <Input data-testid="req-email" className={inputCls} value={form.subject_email}
                  onChange={(e) => setForm((f) => ({ ...f, subject_email: e.target.value }))} placeholder="person@example.com" />
              </div>
              <div className="space-y-2">
                <Label className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Subject Name</Label>
                <Input data-testid="req-name" className={inputCls} value={form.subject_name}
                  onChange={(e) => setForm((f) => ({ ...f, subject_name: e.target.value }))} placeholder="Optional" />
              </div>
              <div className="space-y-2">
                <Label className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Notes</Label>
                <Textarea data-testid="req-notes" className={inputCls} value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Context / channel" />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="req-submit" disabled={saving} onClick={create}
                className="rounded-sm bg-primary hover:bg-primary/90 transition-colors duration-200">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Log Request
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Table */}
      <div className="border border-border rounded-sm bg-card" data-testid="privacy-table">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              {["Type", "Subject", "Product", "Due (SLA)", "Status", ""].map((h, i) => (
                <TableHead key={i} className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {requests.map((r) => (
              <TableRow key={r.id} className={`border-border hover:bg-white/[0.02] ${r.overdue ? "bg-destructive/[0.03]" : ""}`} data-testid="privacy-row">
                <TableCell className="font-mono text-xs capitalize text-white">{r.request_type}</TableCell>
                <TableCell className="text-xs">
                  <div className="text-white">{r.subject_name || "—"}</div>
                  <div className="font-mono text-[11px] text-muted-foreground">{r.subject_email}</div>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{r.product}</TableCell>
                <TableCell data-testid="privacy-sla"><SlaCell r={r} /></TableCell>
                <TableCell><StatusBadge status={r.status} /></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" data-testid="review-btn" onClick={() => setDetail(r)}
                    className="text-primary hover:text-primary hover:bg-primary/10 rounded-sm h-8">
                    Review
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {requests.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-10">
                  No requests in this view.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Detail / action drawer */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-card border-border rounded-sm" data-testid="privacy-detail">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between pr-6">
                  <span className="capitalize">{detail.request_type} request</span>
                  <StatusBadge status={detail.status} />
                </DialogTitle>
                <DialogDescription>Data-subject request for {detail.subject_email}.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div className="border border-border rounded-sm p-3">
                    <div className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground">Subject</div>
                    <div className="mt-1">{detail.subject_name || "—"}</div>
                    <div className="font-mono text-[11px] text-muted-foreground">{detail.subject_email}</div>
                  </div>
                  <div className={`border rounded-sm p-3 ${detail.overdue ? "border-destructive/40 bg-destructive/5" : "border-border"}`}>
                    <div className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground">SLA · {detail.sla_days}d window</div>
                    <div className="mt-1 font-mono text-xs">{detail.due_date ? new Date(detail.due_date).toLocaleDateString() : "—"}</div>
                    <div className={`font-mono text-[11px] mt-0.5 ${detail.overdue ? "text-destructive" : "text-muted-foreground"}`}>
                      {detail.status === "resolved" || detail.status === "rejected"
                        ? "closed"
                        : detail.overdue
                        ? `${Math.abs(detail.days_remaining)}d overdue`
                        : `${detail.days_remaining}d remaining`}
                    </div>
                  </div>
                </div>
                {detail.notes && (
                  <div className="border border-border rounded-sm p-3">
                    <div className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground">Notes</div>
                    <div className="mt-1 text-muted-foreground">{detail.notes}</div>
                  </div>
                )}
                <div className="flex flex-wrap gap-2 pt-2">
                  {detail.status === "pending" && (
                    <Button data-testid="action-review" size="sm" onClick={() => transition(detail.id, { status: "in_review" }, "Moved to review")}
                      className="rounded-sm bg-primary hover:bg-primary/90"><Eye className="w-4 h-4 mr-1" /> Start Review</Button>
                  )}
                  {detail.status !== "resolved" && (
                    <Button data-testid="action-resolve" size="sm" onClick={() => transition(detail.id, { status: "resolved", resolution: "Fulfilled." }, "Request resolved")}
                      className="rounded-sm bg-emerald-600 hover:bg-emerald-600/90 text-white"><Check className="w-4 h-4 mr-1" /> Resolve</Button>
                  )}
                  {detail.status !== "rejected" && detail.status !== "resolved" && (
                    <Button data-testid="action-reject" size="sm" variant="outline" onClick={() => transition(detail.id, { status: "rejected" }, "Request rejected")}
                      className="rounded-sm border-destructive/40 text-destructive hover:bg-destructive/10"><X className="w-4 h-4 mr-1" /> Reject</Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
